/**
 * Common error layout for errors that get displayed on the elemental overpanel or blank pages
 * Use this inside of the Overpanel component:
 *
 * <Overpanel>
 *   <ErrorCommon />
 * </Overpanel>
 *
 * or use it wherever :)
 */

import PropTypes from 'prop-types'

import React from 'react'
import { get } from 'lodash'
import content from 'pp-react-l10n'
import { getFlowType } from '../../transfer/utils/url-utils'
import {
  SUMMARY_PAGE_URL,
  TRANSFER_BASE_URL,
  CUSTOMER_SERVICE_URL,
} from '../constants'
import { tracking } from '../../lib/analytics/mParticle'
import Events from 'lib/analytics/eventTags'
import { ENV_TYPE, P2P_HOMEPAGE_URL } from '../../../../lib/constants'
import { ErrorView } from '@paypalcorp/pp-react'
import { routeTo } from '../utils/routeHelper'
const i18n = content('transfer/guestLogin/errors')

// Currently just used for getting pisp redirect content
// TODO: Shall we move this content elsewhere? Possible content may change.

const i18nSuccess = content('transfer/send/success')
const i18nPreview = content('transfer/page/preview')
const i18nError = content('transfer/send/error')

class ErrorCommon extends React.Component {
  static propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    status: PropTypes.oneOf(['CRITICAL', 'WARNING', 'INFO']),
    header: PropTypes.oneOf(['DEFAULT', 'SENDING_LIMIT', 'CUSTOM_ERROR']),
    customErrorCode: PropTypes.string,
    extra: PropTypes.object,
    rawErrorCode: PropTypes.string,
    env: PropTypes.string,
    hideServiceMessage: PropTypes.bool,
    handleBackButton: PropTypes.func,
    handleLetsTryAgainButton: PropTypes.func,
    handleDismissButton: PropTypes.func,
    showDismissButton: PropTypes.bool,
    serviceTextKey: PropTypes.string,
  }

  static defaultProps = {
    status: 'WARNING',
    headerContent: 'DEFAULT',
    showDismissButton: false,
  }

  componentDidMount() {
    this.recordErrorEvent()
    this.captureLinkClicks()
  }

  handleBackButtonClick = () => {
    const { handleBackButton } = this.props
    if (handleBackButton) {
      handleBackButton()
    }
  }

  handleLetsTryAgainClick = () => {
    const { handleLetsTryAgainButton } = this.props
    if (handleLetsTryAgainButton) {
      handleLetsTryAgainButton()
    }
  }

  recordErrorEvent() {
    const { rawErrorCode, message } = this.props

    let errorDescription = get(
      message,
      'props.dangerouslySetInnerHTML.__html',
      null
    )

    if (typeof message === 'string') {
      errorDescription = message
    }

    tracking(
      Events.p2p_failed_customer_interaction_occurred({
        ...(errorDescription && { error_description: errorDescription }),
        error_code: rawErrorCode,
      })
    )
  }

  openLinkInNewTab(event) {
    event.preventDefault()
    window.open(event.target.href, '_blank')
  }

  captureLinkClicks() {
    const linkElements = document.querySelectorAll(
      '.error-message-with-links a'
    )
    linkElements.forEach(link => {
      link.onclick = this.openLinkInNewTab
    })
  }

  routeToHomepageURL() {
    routeTo(P2P_HOMEPAGE_URL)
  }

  isPispEnv() {
    const { env } = this.props
    return env === ENV_TYPE.PISP
  }

  getTitlesContent() {
    const isPisp = this.isPispEnv()
    const showSuccessRedirect = isPisp === true && getFlowType() === 'request'
    let titleContent
    let subTitleContent

    if (this.props.extra?.fromService) {
      titleContent = this.props.extra.title
      subTitleContent = this.props.extra.message
    } else {
      const headerContent = {
        DEFAULT: i18n('content.header'),
        SENDING_LIMIT: i18n('content.headerSendingLimit'),
        CUSTOM_ERROR: i18nError('error.' + this.props.customErrorCode),
      }

      /** Set the title and subtitle content */
      titleContent = headerContent[this.props.header] || headerContent.DEFAULT
      subTitleContent = this.props.message
      if (showSuccessRedirect) {
        titleContent = this.props.message
        subTitleContent = i18nSuccess.html('success.redirect')
      }
    }

    return {
      titleContent,
      subTitleContent,
    }
  }

  getButtonsContent() {
    const isPisp = this.isPispEnv()
    const amexScreen =
      this.props.customErrorCode === 'amexInsufficientFunds' ||
      this.props.customErrorCode === 'amexSuspectedFraud'

    /** Set the primary button content and CTA handler */
    let primaryButtonContent = '',
      buttonClickHandler,
      tertiaryButtonContent = ''
    if (amexScreen) {
      primaryButtonContent = i18nError('error.button.letsTryAgain')
      buttonClickHandler = this.handleLetsTryAgainClick
    } else if (isPisp) {
      primaryButtonContent = i18nPreview('link.back')
      buttonClickHandler = this.handleBackButtonClick
    } else {
      primaryButtonContent = i18nError('error.button.ok')
      buttonClickHandler =
        this.props.handleDismissButton || this.routeToHomepageURL
    }

    /** Set the tertiary button content and CTA handler */
    if (!amexScreen && !this.props.hideServiceMessage) {
      tertiaryButtonContent = i18n.html(
        this.props.serviceTextKey || 'content.service',
        {
          helpLink: CUSTOMER_SERVICE_URL,
          transferHomePage: TRANSFER_BASE_URL,
          summaryPage: SUMMARY_PAGE_URL,
        }
      )
    }

    return {
      primaryButtonContent,
      buttonClickHandler,
      tertiaryButtonContent,
    }
  }

  render() {
    const { titleContent, subTitleContent } = this.getTitlesContent()
    const {
      primaryButtonContent,
      buttonClickHandler,
      tertiaryButtonContent,
    } = this.getButtonsContent()

    return (
      <ErrorView
        title={titleContent}
        subTitle={subTitleContent}
        {...(primaryButtonContent && {
          primaryButtonOptions: {
            title: primaryButtonContent,
            onClick: () => {
              buttonClickHandler()
            },
          },
        })}
        {...(tertiaryButtonContent && {
          tertiaryButtonOptions: {
            title: tertiaryButtonContent,
            onClick: () => window.open(CUSTOMER_SERVICE_URL),
          },
        })}
      />
    )
  }
}

export default ErrorCommon
